/**
 * React Query mutations for lesson operations
 * Following the proven pattern from use-waste-orders-mutations.ts
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { lessonsAPI } from '../api/lessons.api';
import { lessonQueryKeys } from './useLessonsQueries';
import type { LessonDTO } from '../core/types/lesson.types';
import { getErrorMessage } from '@/core/api/response-handler';

/**
 * Hook for managing lesson mutations (create, update, delete, visibility)
 * Follows the same pattern as waste orders for consistency
 *
 * @example
 * const mutations = useLessonMutations();
 * mutations.create.mutate(newLesson);
 * mutations.update.mutate(updatedLesson);
 */
export function useLessonMutations() {
  const queryClient = useQueryClient();

  /**
   * Mutation for creating a new lesson
   */
  const create = useMutation({
    mutationFn: async (lesson: LessonDTO) => {
      return lessonsAPI.create(lesson);
    },
    onSuccess: (data) => {
      toast.success('Lecția a fost creată cu succes');
      // Invalidate all lesson lists
      queryClient.invalidateQueries({ queryKey: lessonQueryKeys.lists() });
      // Set the new lesson in cache
      queryClient.setQueryData(lessonQueryKeys.detail(data.id!), data);
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la crearea lecției')
      );
    },
  });

  /**
   * Mutation for updating an existing lesson
   */
  const update = useMutation({
    mutationFn: async (lesson: LessonDTO) => {
      return lessonsAPI.update(lesson);
    },
    onSuccess: (data) => {
      toast.success('Lecția a fost actualizată cu succes');
      // Invalidate all lesson queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: lessonQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: lessonQueryKeys.detail(data.id!),
      });
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la actualizarea lecției')
      );
    },
  });

  /**
   * Mutation for deleting a lesson
   */
  const deleteLesson = useMutation({
    mutationFn: async (id: string) => {
      return lessonsAPI.delete(id);
    },
    onSuccess: (_, deletedId) => {
      toast.success('Lecția a fost ștearsă cu succes');
      // Remove from cache
      queryClient.removeQueries({ queryKey: lessonQueryKeys.detail(deletedId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: lessonQueryKeys.lists() });
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la ștergerea lecției')
      );
    },
  });

  /**
   * Mutation for toggling lesson visibility
   */
  const toggleVisibility = useMutation({
    mutationFn: async ({ id, isHidden }: { id: string; isHidden: boolean }) => {
      return lessonsAPI.toggleVisibility(id, isHidden);
    },
    onSuccess: (_, variables) => {
      const action = variables.isHidden ? 'ascunsă' : 'afișată';
      toast.success(`Lecția a fost ${action} cu succes`);
      // Invalidate affected queries
      queryClient.invalidateQueries({
        queryKey: lessonQueryKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: lessonQueryKeys.lists() });
    },
    onError: (error) => {
      toast.error(
        error?.message ||
          'Eroare la modificarea vizibilității lecției'
      );
    },
  });

  /**
   * Mutation for duplicating a lesson
   * Creates a copy with new ID and default values
   */
  const duplicate = useMutation({
    mutationFn: async (lessonId: string) => {
      // First fetch the original lesson
      const original = await lessonsAPI.getById(lessonId);

      // Create a copy with modifications
      const copy: LessonDTO = {
        ...original,
        id: undefined, // Let backend generate new ID
        title: `${original.title} (Copie)`,
        isHidden: true, // Start hidden until edited
      } as any;

      // Save the copy
      return lessonsAPI.create(copy);
    },
    onSuccess: (data) => {
      toast.success('Lecția a fost duplicată cu succes');
      queryClient.invalidateQueries({ queryKey: lessonQueryKeys.lists() });
      queryClient.setQueryData(lessonQueryKeys.detail(data.id!), data);
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(error, 'Eroare la duplicarea lecției')
      );
    },
  });

  /**
   * Optimistic update helper
   * Updates lesson in cache immediately before server response
   */
  const optimisticUpdate = useMutation({
    mutationFn: async (lesson: LessonDTO) => {
      return lessonsAPI.update(lesson);
    },
    onMutate: async (newLesson) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: lessonQueryKeys.detail(newLesson.id!),
      });

      // Snapshot previous value
      const previousLesson = queryClient.getQueryData(
        lessonQueryKeys.detail(newLesson.id!)
      );

      // Optimistically update cache
      queryClient.setQueryData(
        lessonQueryKeys.detail(newLesson.id!),
        newLesson
      );

      // Return context with previous value
      return { previousLesson };
    },
    onError: (error, _, context) => {
      // Rollback on error
      if (context?.previousLesson) {
        queryClient.setQueryData(
          lessonQueryKeys.detail((context.previousLesson as LessonDTO).id!),
          context.previousLesson
        );
      }
      toast.error(
        getErrorMessage(error, 'Eroare la actualizarea lecției')
      );
    },
    onSettled: (data) => {
      // Refetch after success or error
      if (data?.id) {
        queryClient.invalidateQueries({
          queryKey: lessonQueryKeys.detail(data.id),
        });
      }
    },
  });

  return {
    // Mutations
    create,
    update,
    delete: deleteLesson,
    toggleVisibility,
    duplicate,
    optimisticUpdate,

    // Loading states
    isCreating: create.isPending,
    isUpdating: update.isPending,
    isDeleting: deleteLesson.isPending,
    isTogglingVisibility: toggleVisibility.isPending,
    isDuplicating: duplicate.isPending,

    // General loading state
    isLoading:
      create.isPending ||
      update.isPending ||
      deleteLesson.isPending ||
      toggleVisibility.isPending ||
      duplicate.isPending,

    // Error states
    createError: create.error,
    updateError: update.error,
    deleteError: deleteLesson.error,
  };
}
